import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlus, FiEdit3, FiTrash2, FiSearch, FiUser, FiMail, FiPhone, FiGlobe, FiImage } = FiIcons;

const ClientManagement = ({ selectedClient, setSelectedClient, setCurrentView }) => {
  const [clients, setClients] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingClient, setEditingClient] = useState(null);
  const [newClient, setNewClient] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
    industry: '',
    logo: ''
  });

  useEffect(() => {
    const savedClients = localStorage.getItem('albertarank_clients');
    if (savedClients) {
      setClients(JSON.parse(savedClients));
    } else {
      const demoClients = [
        {
          id: 1,
          name: 'Calgary Dental Clinic',
          email: 'info@calgarydentalclinic.com',
          phone: '(403) 555-0123',
          website: 'calgarydentalclinic.com',
          industry: 'Healthcare',
          logo: ''
        }
      ];
      setClients(demoClients);
      localStorage.setItem('albertarank_clients', JSON.stringify(demoClients));
    }
  }, []);

  const saveClients = (updatedClients) => {
    setClients(updatedClients);
    localStorage.setItem('albertarank_clients', JSON.stringify(updatedClients));
  };

  const handleAddClient = () => {
    if (newClient.name && newClient.email) {
      const client = {
        ...newClient,
        id: Date.now()
      };
      const updatedClients = [...clients, client];
      saveClients(updatedClients);
      setNewClient({ name: '', email: '', phone: '', website: '', industry: '', logo: '' });
      setShowAddModal(false);
    }
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setNewClient(client);
    setShowAddModal(true);
  };

  const handleUpdateClient = () => {
    if (newClient.name && newClient.email) {
      const updatedClients = clients.map(client =>
        client.id === editingClient.id ? newClient : client
      );
      saveClients(updatedClients);
      setNewClient({ name: '', email: '', phone: '', website: '', industry: '', logo: '' });
      setShowAddModal(false);
      setEditingClient(null);
    }
  };

  const handleDeleteClient = (clientId) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      const updatedClients = clients.filter(client => client.id !== clientId);
      saveClients(updatedClients);
      if (selectedClient && selectedClient.id === clientId) {
        setSelectedClient(null);
      }
    }
  };

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setCurrentView('dashboard');
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 h-full overflow-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Client Management</h1>
          <p className="text-gray-600">Manage your SEO clients and their reports</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowAddModal(true)}
          className="flex items-center space-x-2 bg-[#00A859] text-white px-4 py-2 rounded-lg hover:bg-[#008a4a] transition-colors"
        >
          <SafeIcon icon={FiPlus} />
          <span>Add Client</span>
        </motion.button>
      </div>

      <div className="mb-6">
        <div className="relative">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00A859] focus:border-transparent"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredClients.map((client) => (
          <motion.div
            key={client.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`bg-white rounded-xl shadow-sm border-2 transition-all duration-200 hover:shadow-md cursor-pointer ${
              selectedClient?.id === client.id ? 'border-[#00A859]' : 'border-gray-200'
            }`}
            onClick={() => handleSelectClient(client)}
          >
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                {client.logo ? (
                  <img src={client.logo} alt={client.name} className="w-12 h-12 rounded-lg object-contain bg-gray-50" />
                ) : (
                  <div className="w-12 h-12 bg-gradient-to-br from-[#00A859] to-[#002B60] rounded-lg flex items-center justify-center">
                    <SafeIcon icon={FiUser} className="text-white text-lg" />
                  </div>
                )}
                <div className="flex space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClient(client);
                    }}
                    className="p-2 text-gray-400 hover:text-[#00A859] transition-colors"
                  >
                    <SafeIcon icon={FiEdit3} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClient(client.id);
                    }}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <SafeIcon icon={FiTrash2} />
                  </button>
                </div>
              </div>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">{client.name}</h3>
              <p className="text-sm text-gray-500 mb-4">{client.industry}</p>

              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiMail} className="text-gray-400" />
                  <span className="truncate">{client.email}</span>
                </div>
                {client.website && (
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiGlobe} className="text-gray-400" />
                    <span className="truncate">{client.website}</span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowAddModal(false);
              setEditingClient(null);
              setNewClient({ name: '', email: '', phone: '', website: '', industry: '', logo: '' });
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-bold text-gray-800 mb-4">
                {editingClient ? 'Edit Client' : 'Add New Client'}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Name *</label>
                  <input
                    type="text"
                    value={newClient.name}
                    onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00A859]"
                    placeholder="Enter business name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL</label>
                  <input
                    type="text"
                    value={newClient.logo}
                    onChange={(e) => setNewClient({ ...newClient, logo: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00A859]"
                    placeholder="https://example.com/logo.png"
                  />
                  <p className="text-xs text-gray-500 mt-1">Paste a direct link to the client's logo image.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    value={newClient.email}
                    onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00A859]"
                    placeholder="Enter email address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={newClient.phone}
                    onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00A859]"
                    placeholder="Enter phone number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                  <input
                    type="url"
                    value={newClient.website}
                    onChange={(e) => setNewClient({ ...newClient, website: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00A859]"
                    placeholder="Enter website URL"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                  <input
                    type="text"
                    value={newClient.industry}
                    onChange={(e) => setNewClient({ ...newClient, industry: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00A859]"
                    placeholder="Enter industry"
                  />
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingClient(null);
                    setNewClient({ name: '', email: '', phone: '', website: '', industry: '', logo: '' });
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={editingClient ? handleUpdateClient : handleAddClient}
                  className="flex-1 px-4 py-2 bg-[#00A859] text-white rounded-lg hover:bg-[#008a4a] transition-colors"
                >
                  {editingClient ? 'Update' : 'Add'} Client
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ClientManagement;